/**
 * Pure selectors over the merged UBP-Q4 context. Three groups:
 *   A. wizard flow (ported from the flow source)
 *   B. per-product reads (ported from the calc source)
 *   C. selection selectors that bridge flow ↔ products
 *
 * Selectors return only data (keys, numbers, enums) — never display strings.
 * Labels and number/currency formatting live in components/strings.ts.
 * Composition is done by calling other selectors with the same `ctx`.
 */
import { calculateIncludedAllowance } from '../calculator/allowances';
import { estimateUsageRange, type UsageRange } from '../calculator/estimate-usage-range';
import { calculateOverageCost, type Bound } from '../calculator/overage-cost';
import {
	getConfigMaxUnitCount,
	getProductPricingConfig,
} from '../calculator/product-pricing-configs';
import { QueryValue } from '../util/url';

import { STEP_ORDER } from './constants';
import { validateOrgUserCount } from './validation';

import type { UbpContextValue } from './context';
import type { BillingCycle, Meter, Product, ProductCategory, Step, UsageType } from './types';
import type { OrgUserCountError } from './validation';
import type { Edition, ProductKey } from '../calculator/types';

export type Selector<T> = (ctx: UbpContextValue) => T;

// --- A. flow selectors ---

export const selectStep: Selector<Step> = (ctx) => ctx.step;
export const selectMeter: Selector<Meter> = (ctx) => ctx.meter;
export const selectUsageType: Selector<UsageType> = (ctx) => ctx.usageType;
export const selectUsage: Selector<number> = (ctx) => ctx.usage;
export const selectOrgUserCount: Selector<number | undefined> = (ctx) => ctx.orgUserCount;
export const selectOrgUserCountError: Selector<OrgUserCountError | undefined> = (ctx) =>
	ctx.orgUserCountError;

export const selectCanAdvanceUsageStep: Selector<boolean> = (ctx) =>
	validateOrgUserCount(selectOrgUserCount(ctx)) === undefined;

export const selectCanAdvanceAllowanceStep: Selector<boolean> = (ctx) =>
	selectSelectedProducts(ctx).length > 0;

export const selectStepIndex: Selector<number> = (ctx) => STEP_ORDER.indexOf(selectStep(ctx));

export const selectIncludedAllowance: Selector<number> = (ctx) =>
	calculateIncludedAllowance({ products: ctx.products, meter: ctx.meter });

/**
 * GWP-12277: Estimated monthly meter usage range for the org, derived from
 * the selected usage profile's band (per-user × orgUserCount for most meters,
 * or an absolute monthly total for AI agent resolutions). Ranges live in
 * `calculator/estimate-usage-range.ts`, sourced from the per-meter content specs.
 */
export const selectEstimatedUsageRange: Selector<UsageRange> = (ctx) =>
	estimateUsageRange({
		meter: selectMeter(ctx),
		usageType: selectUsageType(ctx),
		orgUserCount: selectOrgUserCount(ctx),
		usage: selectUsage(ctx),
	});

const selectOverageCost = (ctx: UbpContextValue) =>
	calculateOverageCost({
		meter: selectMeter(ctx),
		estimatedUsage: selectEstimatedUsageRange(ctx),
		includedAllowance: selectIncludedAllowance(ctx),
	});

/**
 * Total monthly overage cost in USD as a low/high range: the cost of usage
 * beyond the included allowance for the selected meter. Overage units are the
 * difference between the estimated usage range (`selectEstimatedUsageRange`)
 * and the included allowance (`selectIncludedAllowance`), priced through the
 * meter's rate tiers. Each bound is zero when that usage estimate is within
 * the allowance.
 */
export const selectTotalOverageCost: Selector<Bound> = (ctx) => selectOverageCost(ctx).cost;

/**
 * Overage units as a low/high range: how many meter units (credits,
 * resolutions, objects, …) the org's estimated usage exceeds its included
 * allowance by. Each bound is zero when that usage estimate stays within
 * the allowance. Used by the Rovo "What's covered" card to show the gap
 * size ("There's a gap of X–Y Rovo credits").
 */
export const selectTotalOverageUnits: Selector<Bound> = (ctx) =>
	selectOverageCost(ctx).overageUnits;

/**
 * Whether the org's estimated usage exceeds its included allowance — i.e.
 * there is any overage to pay for. Uses the upper bound of the estimated
 * usage range, so it's true whenever even the high estimate spills over the
 * allowance. Drives whether the summary step renders the overage cost panel.
 */
export const selectHasOverage: Selector<boolean> = (ctx) =>
	selectOverageCost(ctx).overageUnits.high > 0;

/**
 * Total monthly cost across all selected products, in USD (or the
 * currency-config ISO). Sums per-unit monthly price × unit count for
 * every selected product. Returns undefined if CCP pricing data hasn't
 * finished loading (any product's per-unit price is missing).
 */
export const selectTotalMonthlyCost: Selector<number | undefined> = (ctx) => {
	if (isCcpDataPending(ctx)) {
		return undefined;
	}
	let total = 0;
	for (const p of ctx.products) {
		const perUnit = ctx.calculation.byProductKey[p.productKey];
		if (perUnit === undefined) {
			// Missing pricing data for at least one product → can't produce
			// an accurate aggregate.
			return undefined;
		}
		total += perUnit * p.inputs.unitCount;
	}
	return total;
};

// --- B. per-product reads ---

export const getRequiredProduct = (ctx: UbpContextValue, productKey: ProductKey): Product => {
	const product = ctx.products.find((p) => p.productKey === productKey);
	if (!product) {
		throw new Error(`No product found for productKey="${productKey}"`);
	}
	return product;
};

export const getProductEdition = (ctx: UbpContextValue, productKey: ProductKey): Edition =>
	getRequiredProduct(ctx, productKey).inputs.edition;

export const getProductUnitCount = (ctx: UbpContextValue, productKey: ProductKey): number =>
	getRequiredProduct(ctx, productKey).inputs.unitCount;

/**
 * Maximum unit count allowed for a product, sourced from the pricing-calculator
 * configs' `signupLimit`. Uses the signup limit of the currently-selected
 * edition's plan (mirroring the uniform calculator's per-plan
 * `isUnitCountAboveMaxThreshold`); when the edition isn't sold for the product
 * it falls back to the largest signup limit across the product's plans.
 */
export const getProductMaxUnitCount = (ctx: UbpContextValue, productKey: ProductKey): number =>
	getConfigMaxUnitCount(getProductPricingConfig(productKey), getProductEdition(ctx, productKey));

export const getProductBillingCycle = (
	ctx: UbpContextValue,
	productKey: ProductKey,
): BillingCycle => getRequiredProduct(ctx, productKey).inputs.billingCycle;

export const getProductPerUnitPerMonth = (
	ctx: UbpContextValue,
	productKey: ProductKey,
): number | undefined => ctx.calculation.byProductKey[productKey];

export const isCcpDataPending = (ctx: UbpContextValue): boolean =>
	ctx.ccpStatus === 'idle' || ctx.ccpStatus === 'fetching';

export const getCurrencyIsoCode = (ctx: UbpContextValue) => ctx.currencyISOCode;

export const getNumberFormatLocale = (ctx: UbpContextValue) => ctx.localeISOCode;

// --- C. selection selectors (flow ↔ products bridge) ---

export const selectSelectedProducts: Selector<Product[]> = (ctx) => ctx.products;

export const selectIsProductSelected =
	(key: ProductKey): Selector<boolean> =>
	(ctx) =>
		ctx.products.some((p) => p.productKey === key);

export const selectProductTabSelectionWarningShown: Selector<boolean> = (ctx) =>
	ctx.productTabSelectionWarningShown;

export const selectCategoryHasSelections =
	(category: ProductCategory): Selector<boolean> =>
	(ctx) =>
		ctx.products.some((p) => getProductPricingConfig(p.productKey).category === category);

export const selectSelectedCategory: Selector<ProductCategory | null> = (ctx) => {
	const first = ctx.products[0];
	return first ? getProductPricingConfig(first.productKey).category : null;
};

export const selectInitialProductsTab: Selector<ProductCategory> = (ctx) =>
	selectSelectedCategory(ctx) ?? 'app';

// --- URL serialization selector (inverse of initialValuesFromQuery) ---

export const selectShareableQuery: Selector<Record<string, QueryValue>> = (ctx) => {
	const products = selectSelectedProducts(ctx);
	const query: Record<string, QueryValue> = {
		step: selectStep(ctx),
		meter: selectMeter(ctx),
		usageType: selectUsageType(ctx),
		usage: selectUsage(ctx),
		orgUserCount: selectOrgUserCount(ctx),
		products: products.map((p) => p.productKey),
	};
	for (const p of products) {
		query[`product.${p.productKey}`] = {
			unitCount: p.inputs.unitCount,
			edition: p.inputs.edition,
			billingCycle: p.inputs.billingCycle,
		};
	}
	return query;
};
